import React from "react";

import { appURLs } from "@/app/app-urls.mjs";
import type { GameDefinition } from "@/app/game-definition.mjs";
import { GAME_SYMBOL_LOL } from "@/game-lol/definition-symbol.mjs";
import routes from "@/game-lol/routes.mjs";
import type { GameOverlayFeature } from "@/hub-overlays/constants.mjs";
import GameIconBoxLoL from "@/inline-assets/game-icon-box-lol.svg";
import FriendGameIconBoxLoL from "@/inline-assets/game-icon-friend-box-lol.svg";
import GameShapeLoL from "@/inline-assets/GameShapeLoL.svg";
import LOL_OVERLAY_ANIMATED_ARROW from "@/inline-assets/lol-overlay-option-animated-arrow.svg";
import LOL_OVERLAY_ARROW from "@/inline-assets/lol-overlay-option-static-arrow.svg";
import IconTierlist from "@/inline-assets/tierlist.svg";

export { GAME_SYMBOL_LOL };

type S = LolOverlaySettings;
export type LolOverlaySettings = typeof LOL_OVERLAY_SETTINGS;

export const LOL_OVERLAY_SHARED_VISIBILITY: GameOverlayFeature<S>[] = [
  {
    text: ["overlays:lol.shared.alwaysVisible", "Always Visible"],
    toggle: { key: ["isAlwaysVisible"] },
    items: [
      {
        show: (settings) => !settings.isAlwaysVisible,
        title: ["overlays:lol.shared.visibilityToggle", "Toggle Key"],
        hotkey: { key: ["srOverlayToggleKey"] },
        hint: [
          "overlays:lol.shared.visibilityToggleHint",
          "Shared between Benchmarking, Minimap Timers, and Jungle Pathing",
        ],
      },
    ],
  },
];

export const LOL_OVERLAY_SHARED_PREFERENCES: GameOverlayFeature<S>[] = [
  {
    text: ["overlays:lol.shared.hideSettingsBar", "Hide Settings Bar"],
    description: [
      "overlays:lol.shared.globalSettingsHint",
      "Shared between Benchmarking and Jungle Pathing",
    ],
    toggle: { key: ["hideSimplified"] },
    items: [
      {
        title: ["overlays:lol.shared.clickThrough", "Click Through Overlay"],
        toggle: { key: ["clickThrough"] },
        text: [
          "overlays:lol.shared.globalSettingsHint",
          "Shared between Benchmarking and Jungle Pathing",
        ],
      },
    ],
  },
];

const LOL_OVERLAY_SETTINGS = {
  benchmarking: {
    csPerMinute: "legacy",
    isChampionLevelEnabled: false,
    isCsPerMinuteEnable: true,
    isGoldPerMinuteEnabled: true,
    isKdaEnabled: true,
    isKillParticipationEnabled: false,
    isSupportItemTimerEnabled: false,
    isVisionPerMinuteEnable: false,
    showCsPerMinute: true,
  },
  bgtransparency: 60,
  bgTransparency: 60,
  calculatedCS: true,
  clickThrough: true,
  csBenchmarks: {
    benchmark: "division",
    leagueDivision: "gold",
    manualSetDivision: false,
    pros: { adc: "doublelift", jng: "svenskeren", mid: "faker", top: "khan" },
  },
  junglePathingBenchmarks: {
    benchmark: "simple",
    manualSetDivision: false,
    leagueDivision: "gold",
  },
  displayOption: "legacy",
  game_resolution_x: 1600,
  game_resolution_y: 900,
  gameresolutionx: -1,
  gameresolutiony: -1,
  hideSimplified: true,
  hovertransparency: 20,
  hoverTransparency: 20,
  isAlwaysVisible: true,
  isArenaAugmentsEnabled: true,
  isHealthRelicOverlayEnabled: true,
  isJunglePathingDisplayOptionsSimpleEnabled: true,
  isJunglePathingOverlayEnabled2023: false,
  isLoadingScreenOverlayEnabled: true,
  isplayerdifferenceenabled: true,
  isSummonersRiftJungleOverlayAlwaysVisible: true,
  isSummonersRiftJungleOverlayEnabled: true,
  isSummonersRiftOverlayEnabled: true,
  isItemDifferenceOverlayEnabled: true,
  isSummonersRiftSkillOverlayEnabled2023: false,
  isSupportOverlayEnabled: true,
  isTrinketEnabled: true,
  isUltimateTimersEnabled: true,
  iswardingmaps: true,
  isyourperspectiveenabled: false,
  junglePathingDisplayOptionManuallySet: false,
  playerdifference: {
    enable: true,
    showTeammate: false,
    yourPerspective: true,
  },
  playerDifference: {
    enable: true,
    showTeammate: false,
    yourPerspective: true,
  },
  srOverlayToggleKey: { code: 9, value: "TAB" },
  suppBenchmarks: {
    benchmark: "division",
    leagueDivision: "challenger",
    manualSetDivision: true,
  },
  ui_lock: false,
  ui_position_x: 1450,
  ui_position_y: 55,
  ui_scale: 1,
  uilock: false,
  uipositionx: -1,
  uipositiony: -1,
  uiscale: 1,
  wardingLocation: { distance: 3000, isStandardLocation: false },
  skillOrder: { displayOption: "animated" },
};

const definition = {
  gameSymbol: GAME_SYMBOL_LOL,
  icon: `${appURLs.CDN_PLAIN}/blitz/ui/images/icons/games/small/LOL-small.svg`,
  iconShape: GameShapeLoL,
  iconBox: GameIconBoxLoL,
  iconBadge: FriendGameIconBoxLoL,
  imageHero: `${appURLs.CDN_PLAIN}/blitz/games/assest/hero-logo/hero-logo-lol.png`,
  imageBanner: `${appURLs.CDN}/blitz/library/banner-league.webp`,
  imageKeyart: `${appURLs.CDN}/blitz/ui/img/game-cover/lol-min.webp`,
  imageWordmark: `${appURLs.CDN}/blitz/ui/img/gamecover/name-lol.webp`,
  isActive: true as const,
  fullName: ["common:games.lol.long", "League of Legends"],
  friendlyName: ["common:games.lol.short", "LOL"],
  preferFriendlyName: false as const,
  shortName: "lol" as const,
  backendName: "LEAGUE" as const,
  gameColor: "#D5A038",
  playerNameHash: true,

  overlayBackground: {
    url: `${appURLs.CDN}/blitz/ui/img/settings/overlay-settings/lol-background.webp`,
    offset: "top right",
  },

  overlays: {
    arenaAugments: {
      key: ["isArenaAugmentsEnabled"],
      title: [
        "overlays:lol.arenaAugmentsPrismatics.title",
        "Arena Augments & Prismatic Items",
      ],
      tags: ["utility"],
      description: [
        "overlays:lol.arenaAugmentsPriismatics.description",
        "Easy and direct access to Arena Augment and Prismatic Item stats as you pick them.",
      ],
      images: {
        large: `${appURLs.CDN}/blitz/ui/img/settings/lolOverlay/lol-arena-augments-portrait.webp`,
        small: `${appURLs.CDN}/blitz/ui/img/settings/lolOverlay/lol-arena-augments-min2.webp`,
      },
      native: true,
      date: new Date(2023, 7, 18),
    },
    healthTimer: {
      key: ["isHealthRelicOverlayEnabled"],
      title: ["overlays:lol.aramHealthTimers.title", "ARAM Health Timers"],
      tags: ["tracker"],
      description: [
        "overlays:lol.aramHealthTimers.description",
        "Track health relic respawn times in the Howling Abyss.",
      ],
      images: {
        large: `${appURLs.CDN}/blitz/ui/img/settings/lolOverlay/aram-health-timers-portrait.webp`,
        small: `${appURLs.CDN}/blitz/ui/img/settings/lol-aram-health-timers-min.webp`,
      },
      native: true,
      date: new Date(500),
    },
    minimapTimer: {
      key: ["isSummonersRiftJungleOverlayEnabled"],
      title: ["overlays:lol.minimapTimers.title", "Minimap Timers"],
      tags: ["tracker"],
      description: [
        "overlays:lol.minimapTimers.description",
        "Track jungle camp & inhibitor respawn times.",
      ],
      images: {
        large: `${appURLs.CDN}/blitz/ui/img/settings/lolOverlay/jungle-timers-portrait.webp`,
        small: `${appURLs.CDN}/blitz/ui/img/settings/lol-jungle-timers-min.webp`,
      },
      Preview: React.lazy(
        () => import("@/library/SimulatedOverlayJungleTimers.jsx"),
      ),
      native: true,
      date: new Date(500),
      features: [...LOL_OVERLAY_SHARED_VISIBILITY],
    },
    loadingScreen: {
      key: ["isLoadingScreenOverlayEnabled"],
      title: ["overlays:lol.loadingScreen.title", "Loading Screen"],
      description: [
        "overlays:lol.loadingScreen.description",
        "Get key insights into allies, enemies, champion matchups and more.",
      ],
      tags: ["tracker"],
      images: {
        large: `${appURLs.CDN}/blitz/ui/img/settings/lolOverlay/loading-screen-portrait.webp`,
        small: `${appURLs.CDN}/blitz/overlay-marketplace/lol-loading-screen-small.webp`,
      },
      date: new Date(2021, 10, 3),
    },
    skillRecommended: {
      key: ["isSummonersRiftSkillOverlayEnabled2023"],
      title: ["overlays:lol.skillOrder.title", "Recommended Skill Order"],
      tags: ["utility"],
      description: [
        "overlays:lol.skillOrder.description",
        "Show which skill to level up in game based on your selected Blitz build.",
      ],
      conflictDisclaimer: [
        "overlays:lol.skillOrder.disclaimer",
        "Disable League's native skill overlay.",
      ],
      images: {
        large: `${appURLs.CDN}/blitz/ui/img/settings/lolOverlay/skill-order-portrait.webp`,
        small: `${appURLs.CDN}/blitz/ui/img/settings/lol-recommended-skill-order-min.webp`,
        featured: `${appURLs.CDN}/blitz/overlay-marketplace/skill-order-featured.webp`,
      },
      Preview: React.lazy(
        () => import("@/library/SimulatedOverlaySkillOrder.jsx"),
      ),
      native: true,
      date: new Date(500),
      features: [
        {
          text: [
            "overlays:lol.skillOrder.options.displayOptions.title",
            "Display Options",
          ],
          items: [
            {
              text: [
                "overlays:lol.skillOrder.options.displayOptions.description",
                "How would you like your overlay to behave?",
              ],
              select: {
                key: ["skillOrder", "displayOption"],
                options: [
                  {
                    value: "animated",
                    Icon: LOL_OVERLAY_ANIMATED_ARROW,
                    text: [
                      "overlays:lol.skillOrder.options.displayOptions.animated",
                      "Animated",
                    ],
                  },
                  {
                    value: "static",
                    Icon: LOL_OVERLAY_ARROW,
                    text: [
                      "overlays:lol.skillOrder.options.displayOptions.static",
                      "Static",
                    ],
                  },
                ],
              },
            },
          ],
        },
      ],
    },
    trinketReminder: {
      key: ["isTrinketEnabled"],
      title: ["overlays:lol.trinket.title", "Trinket Reminder"],
      tags: ["utility", "tracker"],
      description: [
        "overlays:lol.trinket.description",
        "Highlight Trinket when it's ready to be used.",
      ],
      images: {
        large: `${appURLs.CDN}/blitz/ui/img/settings/lolOverlay/trinket-portrait.webp`,
        small: `${appURLs.CDN}/blitz/ui/img/settings/lol-trinket-min.webp`,
      },
      native: true,
      date: new Date(500),
    },
    skillTracker: {
      key: ["isUltimateTimersEnabled"],
      title: ["overlays:lol.ultimateTimers.title", "Ultimate Timers"],
      tags: ["utility"],
      description: [
        "overlays:lol.ultimateTimers.description",
        "Display teammate ultimate timers.",
      ],
      images: {
        large: `${appURLs.CDN}/blitz/ui/img/settings/lolOverlay/ultimate-timers-portrait.webp`,
        small: `${appURLs.CDN}/blitz/ui/img/settings/lol-ultimate-timers-min.webp`,
      },
      native: true,
      date: new Date(500),
    },
    itemDifference: {
      key: ["isItemDifferenceOverlayEnabled"],
      title: ["overlays:lol.itemDifference.title", "Item Value Difference"],
      tags: ["tracker"],
      description: [
        "overlays:lol.itemDifference.description",
        "Easily evaluate and compare the value of players item inventories.",
      ],
      images: {
        large: `${appURLs.CDN}/blitz/ui/img/settings/lol-itemdifference-largev4.webp`,
        small: `${appURLs.CDN}/blitz/ui/img/settings/lol-itemdifference-minv3.webp`,
      },
      native: true,
      date: new Date(2021, 11, 22),
    },
  },

  overlaySettingsKey: "srOverlaySettings",
  overlaySettings: LOL_OVERLAY_SETTINGS,

  IPC: () => import("@/game-lol/init.mjs"),

  settings: () => import("@/game-lol/components/Settings.jsx"),
  routes,

  meta: {
    game: {
      title: [
        "home:meta.home.lol.title",
        "Blitz, the best tracker for players to win League of Legends",
      ],
      description: [
        "home:meta.home.lol.description",
        "Expert recommendations, data-driven match analysis, pro builds, guides, overlays, and competitions — personalized for gamers of any skill level.",
      ],
    },
    overlays: {
      title: [
        "overlays:meta.lol.title",
        "League of Legends Overlays, Tools, LoL In Game Coaching",
      ],
      description: [
        "overlays:meta.lol.description",
        "LoL coaching, ARAM health timers, jungle pathing, minimap timers, ultimate timers, recommended skill order overlays",
      ],

      subtitle: [
        "overlays:meta.lol.subtitle",
        "Get all the information you need to dominate live with League of Legends overlays powered by Blitz. Import builds, benchmark your play, see minimap timers, and more",
      ],
    },
    bg: `${appURLs.CDN}/blitz/ui/img/game-bg/lol-f455c.webp`,
    bgVideo: `${appURLs.CDN_VIDEOS}/homepage/lol-homepage-bg-video.webm`,
    players: "100M+",
    matches: "1B+",
    guides: [
      () => import("@/game-lol/components/HomeStatisticsTile.jsx"),
      () => import("@/game-lol/components/HomeProbuildsTile.jsx"),
    ],
    features: [
      {
        title: ["lol.features.auto-import.title", "Auto-Import Runes & Builds"],
        description: [
          "lol.features.auto-import.description",
          "Automatically import the best runes and builds for every situation",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/lol/auto-import.webp`,
      },
      {
        title: [
          "lol.features.profile-match-history.title",
          "Profile and Match History",
        ],
        description: [
          "lol.features.profile-match-history.description",
          "Track all your stats and view any of your previous matches",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/lol/profile-match-history.webp`,
      },
      {
        title: [
          "lol.features.benchmarking-overlay.title",
          "Benchmarking Overlay",
        ],
        description: [
          "lol.features.benchmarking-overlay.description",
          "Track key performance metrics to better guage performance in real time",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/lol/benchmarking-overlay.webp`,
      },
      // {
      //   title: ["lol.features.blitz-battles.title", "Blitz Battles"],
      //   description: [
      //     "lol.features.blitz-battles.description",
      //     "Play in leaderboard battles for your chance to win prizes",
      //   ],
      //   image: `${appURLs.CDN}/blitz/ui/img/homepage/lol/blitz-battles.webp`,
      // },
      {
        title: [
          "lol.features.aram-overlay.title",
          "ARAM Health Timers Overlay",
        ],
        description: [
          "lol.features.aram-overlay.description",
          "Track health relic respawn times in ARAM",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/lol/aram-overlay.webp`,
      },
      {
        title: ["lol.features.skill-order.title", "Recommended Skill Order"],
        description: [
          "lol.features.skill-order.description",
          "Show which skill to level up in game based on your selected Blitz build",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/lol/skill-order.webp`,
      },
      {
        title: ["lol.features.tierlist.title", "Tier List"],
        description: [
          "lol.features.tierlist.description",
          "Get the most up to date rankings for all top champions by mode",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/lol/tierlist.webp`,
      },
      {
        title: ["lol.features.arena-augments.title", "Arena Augments"],
        description: [
          "lol.features.arena-augments.description",
          "View in-depth statistics for Augments in Arena mode",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/lol/arena-augments.webp`,
      },
      {
        title: ["lol.features.postmatch-analysis.title", "Post Match Analysis"],
        description: [
          "lol.features.postmatch-analysis.description",
          "Blitz analyzes your match to give you key insights that will level up your game",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/lol/postmatch-analysis.webp`,
      },
      {
        title: [
          "lol.features.item-value-difference-overlay.title",
          "Item Value Difference Overlay",
        ],
        description: [
          "lol.features.item-value-difference-overlay.description",
          "Easily evaluate and compare the value of players item inventories",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/lol/item-value-difference-overlay.webp`,
      },
      {
        title: [
          "lol.features.minimap-timers-overlay.title",
          "Minimap Timers Overlay",
        ],
        description: [
          "lol.features.jungle-timers-overlay.description",
          "Track jungle & inhibitor respawn times",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/lol/jungle-timers-overlay.webp`,
      },
      {
        title: [
          "lol.features.ultimate-timers-overlay.title",
          "Ultimate Timers Overlay",
        ],
        description: [
          "lol.features.ultimate-timers-overlay.description",
          "Display teammate ultimate timers",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/lol/ultimate-timers-overlay.webp`,
      },
      {
        title: [
          "lol.features.champion-statistics.title",
          "Champion Statistics",
        ],
        description: [
          "lol.features.champion-statistics.description",
          "View in depth champion statistics like win rate, synergies, and more",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/lol/champion-statistics.webp`,
      },

      {
        title: [
          "lol.features.jungle-pathing-overlay.title",
          "Jungle Pathing Overlay",
        ],
        description: [
          "lol.features.jungle-pathing-overlay.description",
          "Take the most effective jungle camps clearing route to dominate the game",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/lol/jungle-pathing-overlay.webp`,
      },

      {
        title: [
          "lol.features.suggested-picks-bans.title",
          "Suggested Picks & Bans",
        ],
        description: [
          "lol.features.suggested-picks-bans.description",
          "Get Pick and Ban suggestions on power picks, synergies, counters, and more",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/lol/suggested-picks-bans.webp`,
      },
      {
        title: [
          "lol.features.loading-screen-overlay.title",
          "Loading Screen Overlay",
        ],
        description: [
          "lol.features.loading-screen-overlay.description",
          "Get key insights into allies, enemies, champion matchups and more",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/lol/loading-screen-overlay.webp`,
      },
      {
        title: ["lol.features.pro-builds.title", "Pro Builds"],
        description: [
          "lol.features.pro-builds.description",
          "See what builds the pros use",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/lol/pro-builds.webp`,
      },
      {
        title: [
          "lol.features.trinket-reminder-overlay.title",
          "Trinket Reminder Overlay",
        ],
        description: [
          "lol.features.trinket-reminder-overlay.description",
          "Highlight trinket when it's ready to be used",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/lol/trinket-reminder-overlay.webp`,
      },
    ],
    articles: [
      {
        Component: React.lazy(
          () => import("@/game-lol/components/HomeChampionReleaseTile.jsx"),
        ),
        componentProps: {
          championKey: "Yunara",
          releaseDate: new Date(2025, 6, 2),
        },
      },
      {
        Component: React.lazy(
          () => import("@/game-lol/components/HomeChampionReleaseTile.jsx"),
        ),
        componentProps: {
          championKey: "Mel",
          releaseDate: new Date(2025, 1, 23),
        },
      },
      {
        Component: React.lazy(
          () => import("@/game-lol/components/HomeChampionReleaseTile.jsx"),
        ),
        componentProps: {
          championKey: "Ambessa",
          releaseDate: new Date(2025, 1, 6),
        },
      },
    ],
    homeData: () => import("@/game-lol/home-fetch-lol-data.mjs"),
  },

  navLinks: [
    {
      href: `/lol/champions/overview`,
      text: ["lol:champions", "Champions"],
      menu: [
        [
          {
            items: [
              {
                text: [
                  "lol:navlinks.champions.summonersRiftStats",
                  "Summoner’s Rift Stats",
                ],
                href: `/lol/champions/overview`,
              },
              {
                text: ["lol:navlinks.champions.aramStats", "ARAM Stats"],
                href: `/lol/champions/aram`,
              },
              // {
              //   text: ["lol:navlinks.doombots.stats", "Doom Bots Stats"],
              //   href: `/lol/champions/doombots`,
              // },
              {
                text: ["lol:navlinks.champions.arenaStats", "Arena Stats"],
                href: `/lol/champions/arena`,
              },
              {
                text: ["lol:navlinks.champions.synergies", "Synergies"],
                href: `/lol/champions/synergies`,
              },
              // {
              //   text: [
              //     "lol:navlinks.champions.arenaDuosStats",
              //     "Arena Duos Stats",
              //   ],
              //   href: `/lol/champions/arena-duos`,
              // },
              {
                text: [
                  "lol:navlinks.arena.augmentStats",
                  "Arena Augment Stats",
                ],
                href: `/lol/arena/augments`,
              },
              // {
              //   text: [
              //     "lol:meta.champions.titleNexusBlitz",
              //     "Nexus Blitz Stats",
              //   ],
              //   href: `/lol/champions/nexus-blitz`,
              // },
              // {
              //   text: ["lol:meta.champions.titleUrf", "URF Stats"],
              //   href: `/lol/champions/urf`,
              // },

              // {
              //   text: [
              //     "lol:meta.champions.titleOneForAll",
              //     "One For All Stats",
              //   ],
              //   href: `/lol/champions/one-for-all`,
              // },
              // {
              //   href: `/lol/probuilds`,
              //   text: ["common:navigation.proBuilds", "Pro Builds"],
              // },
            ],
          },
        ],
      ],
    },
    {
      href: `/lol/tierlist`,
      Icon: IconTierlist,
      text: ["lol:navlinks.tierList", "Tier List"],
      menu: [
        [
          {
            items: [
              {
                text: ["lol:navlinks.tierlist.rankedSolo", "Ranked Solo"],
                href: `/lol/tierlist`,
              },
              {
                text: ["lol:navlinks.tierlist.aram", "ARAM"],
                href: `/lol/tierlist/aram`,
              },
              {
                text: ["lol:navlinks.tierlist.aramMayhem", "ARAM Mayhem"],
                href: `/lol/tierlist/aram-mayhem`,
              },
              {
                text: ["lol:arena", "Arena"],
                href: `/lol/tierlist/arena`,
              },
              {
                text: ["lol:arenaDuos", "Arena Duos"],
                href: `/lol/tierlist/arena-duos`,
              },
              // {
              //   text: ["lol:queueTypes.nexus_blitz", "Nexus Blitz"],
              //   href: `/lol/tierlist/nexus-blitz`,
              // },
              // {
              //   text: ["lol:queueTypes.urf", "URF"],
              //   href: `/lol/tierlist/urf`,
              // },
              // {
              //   text: ["lol:queueTypes.oneForAll", "One For All"],
              //   href: `/lol/tierlist/one-for-all`,
              // },
            ],
          },
        ],
      ],
    },

    // ARAM Mayhem
    {
      href: `/lol/aram-mayhem-augments`,
      text: ["lol:queueTypes.aram-mayhem", "ARAM Mayhem"],
      tag: ["common:new", "New"],
      menu: [
        [
          {
            items: [
              {
                text: [
                  "lol:navlinks.aramMayhem.tierlist",
                  "ARAM Mayhem Tier List",
                ],
                href: `/lol/tierlist/aram-mayhem`,
                highlight: true,
              },
              // {
              //   text: ["lol:navlinks.aramMayhem.stats", "ARAM Mayhem Stats"],
              //   href: `/lol/champions/aram-mayhem`,
              // },
              {
                text: [
                  "lol:navlinks.aramMayhem.augmentsTierlist",
                  "ARAM Mayhem Augments Tier List",
                ],
                href: `/lol/aram-mayhem-augments`,
              },
            ],
          },
        ],
      ],
    },

    // Arena menu (temporary?)
    {
      href: `/lol/tierlist/arena`,
      text: ["lol:arena", "Arena"],
      menu: [
        [
          {
            items: [
              {
                text: [
                  "lol:navlinks.champions.arenaChampionStats",
                  "Arena Champion Stats",
                ],
                href: `/lol/champions/arena`,
              },
              {
                text: [
                  "lol:navlinks.champions.arenaDuosStats",
                  "Arena Duos Stats",
                ],
                href: `/lol/champions/arena-duos`,
              },
              {
                text: [
                  "lol:navlinks.tierlist.arenaTierList",
                  "Arena Tier List",
                ],
                href: `/lol/tierlist/arena`,
              },
              {
                text: [
                  "lol:navlinks.tierlist.arenaDuosTierList",
                  "Arena Duos Tier List",
                ],
                href: `/lol/tierlist/arena-duos`,
              },
              {
                text: [
                  "lol:navlinks.arena.augmentStats",
                  "Arena Augment Stats",
                ],
                href: `/lol/arena/augments`,
              },
              {
                text: ["lol:navlinks.arena.augmentInfo", "Augment Info"],
                href: `/lol/arena`,
              },
            ],
          },
        ],
      ],
    },

    // Doombots menu (temporary)
    // {
    //   href: `/lol/doombots-leaderboard`,
    //   text: ["lol:queueTypes.doomBots", "Doom Bots"],
    //   tag: ["common:new", "New"],
    //   menu: [
    //     [
    //       {
    //         items: [
    //           {
    //             text: [
    //               "lol:navlinks.doombots.leaderboard",
    //               "Doom Bots Leaderboard",
    //             ],
    //             href: `/lol/doombots-leaderboard`,
    //             highlight: true,
    //           },
    //           {
    //             text: ["lol:navlinks.doombots.stats", "Doom Bots Stats"],
    //             href: `/lol/champions/doombots`,
    //           },
    //           {
    //             text: ["lol:navlinks.doombots.tierlist", "Doom Bots Tier List"],
    //             href: `/lol/tierlist/doombots`,
    //           },
    //         ],
    //       },
    //     ],
    //   ],
    // },

    // Urf menu (temporary)
    // {
    //   href: `/lol/tierlist/urf`,
    //   text: ["lol:queueTypes.urf", "Urf"],
    //   // menu: [
    //   //   [
    //   //     {
    //   //       items: [
    //   //         {
    //   //           text: ["lol:navlinks.champions.urfStats", "URF Stats"],
    //   //           href: `/lol/champions/urf`,
    //   //         },
    //   //         {
    //   //           text: ["lol:navlinks.tierlist.urf", "URF Tier List"],
    //   //           href: `/lol/tierlist/urf`,
    //   //         },
    //   //       ],
    //   //     },
    //   //   ],
    //   // ],
    // },

    // One For All menu (temporary)
    // {
    //   href: `/lol/champions/one-for-all`,
    //   text: ["lol:queueTypes.oneForAll", "One For All"],
    //   menu: [
    //     [
    //       {
    //         items: [
    //           {
    //             text: [
    //               "lol:navlinks.champions.oneForAllStats",
    //               "One For All Stats",
    //             ],
    //             href: `/lol/champions/one-for-all`,
    //           },
    //           {
    //             text: [
    //               "lol:navlinks.tierlist.oneForAll",
    //               "One For All Tier List",
    //             ],
    //             href: `/lol/tierlist/one-for-all`,
    //           },
    //         ],
    //       },
    //     ],
    //   ],
    // },

    // Nexus Blitz menu (temporary)
    // {
    //   href: `/lol/champions/nexus-blitz`,
    //   text: ["lol:queueTypes.nexus_blitz", "Nexus Blitz"],
    //   menu: [
    //     [
    //       {
    //         items: [
    //           {
    //             text: [
    //               "lol:meta.champions.titleNexusBlitz",
    //               "Nexus Blitz Stats",
    //             ],
    //             href: `/lol/champions/nexus-blitz`,
    //           },
    //           {
    //             text: [
    //               "lol:meta.tierlist.titleNexusBlitz",
    //               "Nexus Blitz Tier List",
    //             ],
    //             href: `/lol/tierlist/nexus-blitz`,
    //           },
    //         ],
    //       },
    //     ],
    //   ],
    // },
    {
      href: `/overlays/lol`,
      text: ["common:navigation.overlays", "Overlays"],
      excludeSearch: true,
    },
  ],

  promoFeatures: [
    {
      key: "lol-send-damage-to-chat-asdfasdf",
      date: new Date("2024-06-20"),
      image: `${appURLs.CDN}/blitz/ui/img/message-damage-to-chat3.webp`,
      title: ["lol:shareDamageToChat", "Share Damage Breakdown"],
      description: [
        "lol:promoFeature.shareDamageToChat",
        "One-click macro to share your team or the enemy team's damage to the League Client chat to keep your team in the know.",
      ],
      // link: "/overlays/lol?select=minimapTimer",
      // linkText: ["common:overlay.customizeSettings", "Customize Settings"],
    },
  ],

  homepageFeatures: [
    "blitz-search",
    {
      type: "contentful-articles",
      title: ["common:news.title", "News"],
      description: [
        "common:news.descriptionGame",
        "What’s new with {{gameName}}",
        {
          gameName: "League of Legends",
        },
      ],
      cta: {
        href: "/lol/news",
        title: ["common:contentfularticles.viewAllNews", "View All News"],
      },
    },
    "blitz-guides",
    "blitz-articles",
    {
      type: "contentful-grid",
      title: ["lol:championGuides.title", "Champion Guides"],
      description: [
        "lol:championGuides.description",
        "Stay up to date with the latest League of Legends Champion guides",
      ],
      cta: {
        href: "/lol/guides",
        title: ["common:contentfularticles.viewAllGuides", "View All Guides"],
      },
    },
    "blitz-game-features",
  ],
} satisfies GameDefinition<typeof GAME_SYMBOL_LOL>;

export default definition;
